import type { GetWebcastSummaryManagerQuery } from '../../../../../../generated/graphql-manager';

import { ClockIcon, PeakIcon, PlayerIcon, UsersMultipleIcon } from '@movingimage-evp/mi-ui-component-library';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PostLiveEventInfobox } from '../../../../../components/post-live-event-infobox';

import styles from './event-summary-metrics.module.css';

type EventSummaryMetricsProps = {
  webcast: GetWebcastSummaryManagerQuery['webcast'];
  session?: GetWebcastSummaryManagerQuery['session'];
};

export function EventSummaryMetrics({ webcast, session }: EventSummaryMetricsProps) {
  const { t } = useTranslation();
  const [streamingTime, setStreamingTime] = useState('00:00:00');
  const [formattedAveragePlaytime, setFormattedAveragePlaytime] = useState<string | undefined>('00:00:00');
  const averagePlaytimeAvailable = formattedAveragePlaytime !== undefined;

  useEffect(() => {
    if (session?.__typename !== 'GetSessionSuccess') return;

    const startStreaming = new Date(session.startDate);
    const endStreaming = session.endDate && new Date(session.endDate);

    const timeDifference = endStreaming && endStreaming.getTime() - startStreaming.getTime();

    if (!timeDifference) return;

    const hours = Math.floor(timeDifference / (1000 * 60 * 60));
    const minutes = Math.floor((timeDifference % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((timeDifference % (1000 * 60)) / 1000);

    // Format the time as hh:mm:ss
    const formattedTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds
      .toString()
      .padStart(2, '0')}`;

    setStreamingTime(formattedTime);
  }, [session]);

  useEffect(() => {
    const avgPlaytime = webcast.analytics?.avgPlaytime || 0;

    if (avgPlaytime < 0) return setFormattedAveragePlaytime(undefined);

    const hours = Math.floor(avgPlaytime / 60);
    const minutes = Math.floor(avgPlaytime % 60);
    const seconds = Math.floor((avgPlaytime % 1) * 60);

    const formattedAvgPlaytime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds
      .toString()
      .padStart(2, '0')}`;

    setFormattedAveragePlaytime(formattedAvgPlaytime);
  }, [webcast]);

  return (
    <div className={styles.metricsContainer}>
      <PostLiveEventInfobox
        title={t('manager.eventSummary.metrics.streamingTime')}
        icon={<ClockIcon />}
        value={streamingTime}
        metricType="streaming-time"
      />

      <PostLiveEventInfobox
        title={t('manager.eventSummary.metrics.averageViewTime')}
        icon={<PlayerIcon />}
        value={formattedAveragePlaytime}
        placeholder={
          averagePlaytimeAvailable ? undefined : t('manager.eventSummary.metrics.averageViewTimePlaceholder')
        }
        metricType="average-view-time"
      />

      <PostLiveEventInfobox
        title={t('manager.eventSummary.metrics.viewers')}
        icon={<UsersMultipleIcon />}
        value={webcast.analytics?.uniqueUsers || 0}
        metricType="viewers"
      />

      <PostLiveEventInfobox
        title={t('manager.eventSummary.metrics.peakViewers')}
        icon={<PeakIcon />}
        value={webcast.analytics?.peakActivePlays || 0}
        metricType="peak-viewers"
      />
    </div>
  );
}
